import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ACCOUNT_TYPES, balanceToSave, editableAccountType } from "@/lib/accounts";
import {
  ResponsiveDialog, ResponsiveDialogContent, ResponsiveDialogDescription,
  ResponsiveDialogFooter, ResponsiveDialogHeader, ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DecimalInput } from "@/components/ui/decimal-input";
import { Label } from "@/components/ui/label";

const emptyForm = () => ({ name: "", type: "secondary", current_balance: null });

export default function AccountDialog({ open, onOpenChange, account, onlyAccount, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(account ? {
      name: account.name,
      type: editableAccountType(account.type),
      current_balance: account.current_balance ?? null,
    } : emptyForm());
  }, [open, account]);

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Enter an account name");

    const payload = { name, type: form.type };
    const balance = balanceToSave(account, form.current_balance);
    if (balance !== undefined) payload.current_balance = balance;
    setSaving(true);
    try {
      if (account) {
        await api.put(`/accounts/${account.id}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/accounts", payload);
        toast.success("Account added");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="bg-card border-border sm:max-w-lg" data-testid="account-dialog">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle className="font-heading tracking-tight">
            {account ? "Edit account" : "New account"}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="text-muted-foreground">
            {account
              ? "Changes apply to the whole ledger, including your current balance."
              : "Choose the role this account plays in your finances."}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Account name</Label>
            <Input
              autoFocus
              maxLength={60}
              className="mt-1 bg-background"
              value={form.name}
              data-testid="account-name-input"
              onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))}
              onKeyDown={(event) => event.key === "Enter" && submit()}
              placeholder="e.g. Tatra banka"
            />
          </div>

          <fieldset>
            <legend className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Account role</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="account-type-options">
              {Object.entries(ACCOUNT_TYPES).map(([key, meta]) => {
                const Icon = meta.icon;
                const active = form.type === key;
                const disabled = Boolean(account && onlyAccount && key !== "main");
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    disabled={disabled}
                    data-testid={`account-type-${key}`}
                    onClick={() => setForm((value) => ({ ...value, type: key }))}
                    className={`flex items-start gap-3 rounded-md border p-3 text-left transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                      active
                        ? "border-income bg-income/10 ring-1 ring-income/40 hover:bg-income/15"
                        : disabled
                          ? "border-border opacity-40 cursor-not-allowed"
                          : "border-border hover:bg-secondary/60"
                    }`}
                  >
                    <Icon size={19} weight={active ? "fill" : "regular"} className="mt-0.5 shrink-0" />
                    <span>
                      <span className="block text-sm font-semibold">{meta.label}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        {meta.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {account && onlyAccount
                ? "Add another account before changing the role of your main account."
                : "There is always one main account. Choosing Main here makes the current one secondary."}
            </p>
          </fieldset>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Current balance (€)</Label>
            <DecimalInput
              allowNegative
              className="mt-1 bg-background tabular-nums"
              value={form.current_balance}
              data-testid="account-balance-input"
              onValueChange={(balance) => setForm((value) => ({ ...value, current_balance: balance }))}
              onKeyDown={(event) => event.key === "Enter" && submit()}
              placeholder="0.00"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              {account
                ? "What the account really holds today. Saving a different number re-anchors the balance here: older entries stop affecting it, and it continues from this figure."
                : "What the account holds today. Leave it empty if you will import past transactions — the balance then comes from them."}
            </p>
          </div>
        </div>

        <ResponsiveDialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="account-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-income text-black hover:bg-income/90 font-semibold"
            data-testid="save-account-btn"
          >
            {saving ? "Saving…" : account ? "Save changes" : "Add account"}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
